// ЭКРАН ШАГА 1 — лендинг, СОСТОЯНИЕ 1 (`begin`). Тикет B3-2 `34a19875cbb8812ab7fed13c94ef7806`.
//
// ИСТОЧНИК — борд `docs/design/wizard-s1/Life Map - Step 01 Landing v2.html` (живой канон,
// sha `bdb798df…` по `docs/design/WIZARD-CANON.md`), первый кадр «состояние 1 · ключ пуст».
// Копи сверяется с байтом борда машинно: `src/render/__parity__/wizard-s1-copy.test.jsx`.
// Спека — `🧩 s1 — Entry` `3b419875cbb88163b403c50eb7594894`.
//
// ЧТО ЗДЕСЬ ЕСТЬ: композиция сверху вниз — slogan-pair (две ступени), сабхед, место под
// teaser-кривую, три промиса hairline-списком, мета в две строки.
//
// ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ:
//   · СЧЁТЧИК `01 / 07` и КНОПКА `begin` — их печатает ОБОЛОЧКА (`WizardShell.jsx`,
//     `footerNav.js`). У борда они живут в статус-баре и в липком `.cta-row`, то есть ровно в
//     тех двух полосах хрома, которые оболочка держит на всех семи шагах. Дублировать их здесь
//     значило бы завести вторую правду о том, где стоит счётчик;
//   · КРИВАЯ — B3-3 `3e419875cbb881f7942dc5be91962a6e`. Здесь пустой узел нужной высоты: место
//     под неё занято сразу, иначе приход кривой сдвинет всё, что ниже, и композицию придётся
//     пересверять. Туда же уедет `land-wash` борда (амбер-подсветка за заголовком) — это
//     атмосфера того же слоя, а не отдельный блок композиции;
//   · СОСТОЯНИЕ 2 (`continue →` + `start over`) — B3-4 `3e419875cbb881acae99e814d0d6dfc5`.
//     Оно держится на `hasWork`, то есть на СОХРАНЁННОМ конверте, и без него экран показывал бы
//     состояние 2 на подложенных данных: зелёные тесты и мёртвая кнопка в браузере;
//   · `s1.proof_line` — слот отложен решением, не забыт. Ставить заглушку на экран, который
//     просит доверия, дороже пустого места (спека s1, «Открытое»);
//   · СТРОКА ХРАНИЛИЩА (`s889.d5`, `storageNotice`) — места под неё в борде v2 нет вовсе, а
//     придумывать его на замороженном экране нельзя. Ушла хвостом CD (`s885.d2`), см. бриф.
//
// ⚠️ СЖИМАЕМАЯ ПОЛОСА — ТОЛЬКО `ul.bullets` (хром §1, борд `7b27745`). Мета прижата к низу
// через `mt-auto`, поэтому корень экрана обязан быть колонкой на всю высоту тела оболочки:
// `min-h-full` здесь несущий, а не косметика.
//
// ⛔ ПОРЯДОК ПРОМИСОВ ОБЯЗАТЕЛЕН ГРАММАТИЧЕСКИ: `them` во второй строке указывает на `stages`
// из первой. Перестановка ломает не ритм, а ссылку.

/** Волосяная линия борда: `rgba(232,226,212,.10)` — `--paper` на десятой доле непрозрачности. */
const HAIRLINE = 'border-[rgba(232,226,212,0.10)]';

export default function StepOne() {
  return (
    <div data-testid="step-1" className="s1-screen flex min-h-full flex-col">
      {/* ⭐ ДВЕ ПОДСВЕТКИ БОРДА, ОБЕ В СОСТОЯНИИ 1 (строки 121–122 файла борда).
          Широкую даёт `.s1-screen::before`, плотное пятно за заголовком — узел ниже.
          Пропущены в первой сдаче B3-2 и поставлены после замечания web-Claude: я отнёс их
          к декорациям, отложенным вместе с кривой, а они стоят в кадре состояния 1 — то есть
          входили в скоуп экрана с самого начала. Вид — `src/styles/wizard-s1.css`. */}
      <div className="s1-wash" aria-hidden="true" data-testid="s1-wash" />

      <h1
        className="mt-[30px] font-serif font-normal italic text-[var(--paper)]"
        style={{ fontVariationSettings: '"opsz" 56' }}
      >
        <span
          data-testid="s1-title-1"
          className="block text-[44px] leading-[1] tracking-[-0.018em]"
        >
          Life Map.
        </span>
        <span
          data-testid="s1-title-2"
          className="mt-[2px] block text-balance text-[36px] leading-[1.04] tracking-[-0.018em] text-[var(--amber)]"
        >
          The trail you're leaving behind
        </span>
      </h1>

      <p
        data-testid="s1-subhead"
        className="mt-[13px] max-w-[280px] text-pretty font-serif text-[18px] leading-[1.34] text-[var(--paper-dim)]"
        style={{ fontVariationSettings: '"opsz" 18' }}
      >
        You've never seen it whole.
      </p>

      {/* TEASER-КРИВАЯ, вариант B (B3-3). Порт байта борда: геометрия путей, градиенты и маска
          — как в файле; вид и анимации — `src/styles/wizard-s1.css`, оба сверяются с
          бордом машинно (`wizard-s1-style.test.jsx`).

          ⚠️ РАЗМЕТКУ ЗДЕСЬ НЕ «УЛУЧШАТЬ». Маска `#s1teaseMask` — это и есть растворение обоих
          концов; убрать её ради «чистого SVG» значит дорисовать концы, то есть сделать ровно
          то, что спека s1 запрещает поимённо. Ни тиков, ни точки «you are here» здесь нет и
          не должно быть.

          ⚠️ `preserveAspectRatio="none"` — байт борда, а не недосмотр: кривая тянется по ширине
          узла, и вместе с ней неравномерно тянется ОБВОДКА. Спека называет это долгом сборки
          («проверить геометрию»), поэтому замерено в настоящем браузере, а не прикинуто:
          на 375px масштаб X = 0.9732, Y = 0.871, то есть объявленные 1.1 приходят на экран как
          1.071 на вертикальных участках и 0.958 на горизонтальных — расхождение 0.112px.
          Долг закрывается ответом «неравномерность есть и она в пределах десятой доли пикселя»,
          а НЕ правкой: у самого борда она БОЛЬШЕ (там X = 1.0 при том же Y, то есть 0.142px),
          потому что виновата не наша ширина, а отношение viewBox 336×62 к полосе высотой 54.
          Выпрямить её можно только сменой `preserveAspectRatio` — то есть правкой байта CD. */}
      <div data-testid="s1-teaser" aria-hidden="true" className="s1-teaser">
        <svg viewBox="0 0 336 62" preserveAspectRatio="none">
          <defs>
            <linearGradient id="s1teaseF" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#D48A3C" stopOpacity="1" />
              <stop offset="1" stopColor="#C8482B" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="s1teaseS" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#D48A3C" stopOpacity="0" />
              <stop offset=".16" stopColor="#D48A3C" stopOpacity="1" />
              <stop offset=".74" stopColor="#D48A3C" stopOpacity="1" />
              <stop offset="1" stopColor="#D48A3C" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="s1teaseM" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#fff" stopOpacity="0" />
              <stop offset=".16" stopColor="#fff" stopOpacity="1" />
              <stop offset=".74" stopColor="#fff" stopOpacity="1" />
              <stop offset="1" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <mask id="s1teaseMask" maskUnits="userSpaceOnUse" x="0" y="0" width="336" height="62">
              <rect x="0" y="0" width="336" height="62" fill="url(#s1teaseM)" />
            </mask>
          </defs>
          <path className="ghost g1" d="M0 40 C 40 22, 70 46, 110 30 C 150 14, 180 44, 220 26 C 260 8, 300 34, 336 22" mask="url(#s1teaseMask)" />
          <path className="ghost g2" d="M0 50 C 40 44, 70 30, 110 42 C 150 54, 180 28, 220 40 C 260 52, 300 22, 336 34" mask="url(#s1teaseMask)" />
          <path className="ghost g3" d="M0 34 C 40 48, 70 20, 110 38 C 150 56, 180 18, 220 32 C 260 46, 300 28, 336 30" mask="url(#s1teaseMask)" />
          <path className="tease-fill" mask="url(#s1teaseMask)" d="M0 46 C 34 42, 52 21, 82 23 C 112 25, 124 50, 156 49 C 188 48, 196 14, 232 12 C 268 10, 286 35, 336 27 L336 62 L0 62 Z" />
          <path className="tease-line" d="M0 46 C 34 42, 52 21, 82 23 C 112 25, 124 50, 156 49 C 188 48, 196 14, 232 12 C 268 10, 286 35, 336 27" />
        </svg>
      </div>

      <ul
        data-testid="s1-promises"
        className={`mt-[18px] flex list-none flex-col border-y p-0 ${HAIRLINE}`}
      >
        {[
          // ⚠️ Апостроф — ASCII `'`, как в борде (типографских `’` в файле ноль). Подмена на
          // «красивый» сломала бы парити копи, и красное объяснялось бы чем угодно.
          "Every stage you've lived, assembled into one chain",
          'All you do is describe them',
          'Find what was hiding in plain sight',
        ].map((line) => (
          <li
            key={line}
            className={`list-none border-t py-[11px] font-serif text-[14px] italic leading-[1.4] text-[var(--paper-dim)] first:border-t-0 ${HAIRLINE}`}
            style={{ fontVariationSettings: '"opsz" 14' }}
          >
            {line}
          </li>
        ))}
      </ul>

      {/* ⚠️ `whitespace-nowrap` — не вкус: трекинг `.16em` на подменённом шрифте расползается
          молча, и моно-строка уезжает в две (`_CC-INDEX.md`, «хром»). */}
      <div
        data-testid="s1-meta"
        className={`mt-auto flex flex-col gap-[4px] whitespace-nowrap border-t border-dashed pt-[12px] font-mono text-[10px] uppercase leading-[1.7] tracking-[0.16em] text-[var(--paper-mute)] border-[var(--ink-600)]`}
      >
        <div>
          about <b className="font-medium text-[var(--paper)]">12 min</b> · no prep · private
        </div>
        <div>
          a <b className="font-medium text-[var(--paper)]">7-page</b> map of your life
        </div>
      </div>
    </div>
  );
}
